import React, { useState, useEffect, useRef } from 'react';
import { assignFinancialBid, getFinanceManagers } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import { useFocusTrap } from '../hooks/useFocusTrap';
import './TenderList.css';

function AssignFinancialBidModal({ tender, onClose, onSuccess }) {
  const { toast } = useToast();
  const modalRef = useRef(null);
  const [financeManagers, setFinanceManagers] = useState([]);
  const [form, setForm] = useState({ assignedToId: '', instructions: '', dueDate: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleClose = () => {
    if (loading) return;
    setError(null);
    onClose();
  };

  const isOpen = !!tender;
  useModalScrollLock(isOpen);
  useFocusTrap(modalRef, isOpen, handleClose);

  useEffect(() => {
    if (!tender) return;
    let cancelled = false;
    getFinanceManagers()
      .then((res) => {
        if (!cancelled && res.success) setFinanceManagers(res.users || []);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [tender]);

  const handleAssign = async () => {
    if (!tender || !form.assignedToId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await assignFinancialBid(tender.id, {
        assigned_to_id: form.assignedToId,
        instructions: form.instructions || undefined,
        due_date: form.dueDate || undefined,
      });
      if (response.success) {
        toast.success('Financial bid assigned successfully');
        onSuccess?.(response.financial_bid);
        onClose();
      }
    } catch (err) {
      const message = err?.data?.error || err?.message || 'Failed to assign price bid';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (!tender) return null;

  return (
    <div className="modal-overlay" onClick={handleClose} role="presentation">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-fb-modal-title"
        className="confirm-modal modal--sm"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="assign-fb-modal-title">Assign Price Bid</h3>
        <p>Tender: <strong>{tender.tender_ref}</strong></p>
        {error && (
          <div className="error-message" style={{ marginBottom: '12px' }}>
            {error}
            <button type="button" onClick={() => setError(null)}>×</button>
          </div>
        )}
        <div style={{ margin: '16px 0' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Finance User *</label>
          <select
            value={form.assignedToId}
            onChange={(e) => setForm((f) => ({ ...f, assignedToId: e.target.value }))}
            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
          >
            <option value="">Select finance user</option>
            {financeManagers.map((u) => (
              <option key={u.id} value={u.id}>{u.full_name || u.username || u.email}</option>
            ))}
          </select>
        </div>
        <div style={{ margin: '16px 0' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Instructions</label>
          <textarea
            value={form.instructions}
            onChange={(e) => setForm((f) => ({ ...f, instructions: e.target.value }))}
            placeholder="Assignment notes..."
            rows={3}
            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db', resize: 'vertical' }}
          />
        </div>
        <div style={{ margin: '16px 0' }}>
          <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>Due Date</label>
          <input
            type="date"
            value={form.dueDate}
            onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))}
            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
          />
        </div>
        <div className="modal-actions">
          <button className="cancel-btn" onClick={handleClose} disabled={loading}>Cancel</button>
          <button className={`confirm-go-ahead-btn ${loading ? 'btn-loading' : ''}`} onClick={handleAssign} disabled={loading || !form.assignedToId}>
            {loading ? 'Assigning…' : 'Assign'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AssignFinancialBidModal;
